import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { invoiceSchema, type InvoiceFormData } from '../schemas/invoiceSchema'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { Button } from '@/components/ui/Button'

interface InvoiceFormProps {
  customerId: string
  initialData?: any
  onSubmit: (data: InvoiceFormData) => Promise<void>
  onCancel: () => void
  loading?: boolean
}

export function InvoiceForm({ customerId, initialData, onSubmit, onCancel, loading }: InvoiceFormProps) {
  const { register, handleSubmit, formState: { errors } } = useForm<InvoiceFormData>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      customer_id: customerId,
      status: 'outstanding',
      ...initialData
    },
  })

  const statusOptions = [
    { value: 'outstanding', label: 'Outstanding' },
    { value: 'partial_payment', label: 'Partial Payment' },
    { value: 'paid', label: 'Paid' },
    { value: 'disputed', label: 'Disputed' },
    { value: 'written_off', label: 'Written Off' },
  ]

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input 
        label="Invoice Number" 
        {...register('invoice_number')} 
        error={errors.invoice_number?.message} 
        required 
      />
      
      <div className="grid grid-cols-2 gap-4">
        <Input 
          label="Invoice Date" 
          type="date" 
          {...register('invoice_date')} 
          error={errors.invoice_date?.message} 
          required 
        />
        <Input 
          label="Due Date" 
          type="date" 
          {...register('due_date')} 
          error={errors.due_date?.message} 
          required 
        />
      </div>

      <Input
        label="Original Amount"
        type="number"
        step="0.01"
        {...register('original_amount', { valueAsNumber: true })}
        error={errors.original_amount?.message}
        required
      />

      <Select
        label="Status"
        {...register('status')}
        options={statusOptions}
        error={errors.status?.message}
      />

      <Textarea
        label="Description"
        {...register('description')}
        error={errors.description?.message}
        placeholder="Optional notes about this invoice"
      />

      <div className="flex justify-end space-x-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={loading}>{initialData ? 'Update' : 'Create'} Invoice</Button>
      </div>
    </form>
  )
}
